// frontend/src/components/Enroll/PaymentStep.js
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal/Modal';
import styled from 'styled-components';
import Typography from '../Typography';
import Button from '../Button/Button';
import axiosClient from '../../api/axiosClient';
import PaymentModal from './PaymentModal';
import { Elements } from '@stripe/react-stripe-js';
import { getStripePromise } from '../../config/stripePromise';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const PaymentStep = ({
  eventId,
  ticket,
  vehicle,
  initialPaymentSession,
  onPaymentSessionCreated,
  onPaymentStateChange,
  onCancel
}) => {
  const { t } = useTranslation('eventEnroll');
  const [loading, setLoading] = useState(false);
  const [paymentSession, setPaymentSession] = useState(initialPaymentSession || null);

  useEffect(() => {
    if (initialPaymentSession?.clientSecret) {
      setPaymentSession(initialPaymentSession);
    }
  }, [initialPaymentSession]);

  const handleCreatePaymentIntent = async () => {
    if (!ticket?._id) {
      toast.error(t('paymentStep.errors.missingTicket'));
      return;
    }

    setLoading(true);
    try {
      const response = await axiosClient.post('/api/stripe/create-payment-intent', {
        eventId,
        ticketId: ticket._id,
        vehicleId: vehicle?._id || null,
      });
      if (response.data.success) {
        setPaymentSession({
          clientSecret: response.data.clientSecret,
          connectedAccountId: response.data.connectedAccountId,
          paymentId: response.data.paymentId,
          reservationId: response.data.reservationId,
        });
        onPaymentSessionCreated?.({
          clientSecret: response.data.clientSecret,
          connectedAccountId: response.data.connectedAccountId,
          paymentId: response.data.paymentId,
          reservationId: response.data.reservationId,
        });
      } else {
        toast.error(response.data.message || t('paymentStep.errors.createPayment'));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('paymentStep.errors.createPayment'));
    } finally {
      setLoading(false);
    }
  };

  if (paymentSession?.clientSecret) {
    const elementsOptions = {
      clientSecret: paymentSession.clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#10110f',
          // fontFamily: 'Ideal Sans, system-ui, sans-serif',
          fontFamily: 'MonaSans, system-ui, sans-serif',
          spacingUnit: '4px',
          borderRadius: '8px'
        }
      }
    };

    return (
      <Elements
        stripe={getStripePromise(paymentSession.connectedAccountId)}
        options={elementsOptions}
      >
        <PaymentModal
          paymentSession={paymentSession}
          onPaymentResult={onPaymentStateChange}
          onClose={onCancel}
        />
      </Elements>
    );
  }

  return (
    <Modal title={t('paymentStep.title')} onClose={onCancel} maxWidth="500px">
      <Container>
        <Typography $variant="body-1-regular">
          {t('paymentStep.instruction', { price: ticket.price })}
          {/* Para completar la inscripción, realiza el pago de {ticket.price} €. */}
        </Typography>
        <Button onClick={handleCreatePaymentIntent} disabled={loading} size="l">
          {loading ? t('paymentStep.processing') : t('paymentStep.confirmPayment')}
        </Button>
        <Button onClick={onCancel} $variant="outline" size="l">
          {/* Cancelar */}
          {t('paymentStep.cancel')}
        </Button>
      </Container>
    </Modal>
  );
};

PaymentStep.propTypes = {
  eventId: PropTypes.string.isRequired,
  ticket: PropTypes.object,
  vehicle: PropTypes.object,
  initialPaymentSession: PropTypes.shape({
    clientSecret: PropTypes.string,
    connectedAccountId: PropTypes.string,
    paymentId: PropTypes.string,
    reservationId: PropTypes.string,
  }),
  onPaymentSessionCreated: PropTypes.func,
  onPaymentStateChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default PaymentStep;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  width: 100%;
`;
